import type { Piece, DamageModifiers } from '../types';
import { ItemType, MaterialType } from '../types';
import {
  woodResist,
  craftStationResist,
  cookingResist,
  chestResist,
  ironResist,
  marbleResist,
  stoneResist,
  darkwoodStructureRecipe,
  ironStructureRecipe,
  stoneStructureRecipe,
  woodStructureRecipe,
  wearStructure,
  woodRoofStructureWear,
  darkwoodStructureWear,
  stoneStructureWear,
  torchResist,
  woodStructureWear,
  ashwoodStructureRecipe,
  graustenStructureRecipe,
  graustenStructureWear,
  ironStructureWear,
  ashwoodResist,
} from '../model/building';
import { dmg, mods } from '../model/game';
import { SkillType } from '../model/skills';

const damageModifiersElementalPart = {
  fire: 'immune',
  frost: 'immune',
  lightning: 'normal',
  poison: 'immune',
  spirit: 'immune',
} as const;

const damageModifiersFireplace: DamageModifiers = {
  blunt: 'normal',
  slash: 'normal',
  pierce: 'normal',
  chop: 'normal',
  pickaxe: 'normal',
  ...damageModifiersElementalPart,
};

const damageModifieresExtensions: DamageModifiers = mods([0, 0, 1, 2, 0, 0, 1, 0, 3, 3]);

const damageModifiersWood: DamageModifiers = {
  blunt: 'normal',
  slash: 'normal',
  pierce: 'resistant',
  chop: 'weak',
  pickaxe: 'normal',
  fire: 'normal',
  frost: 'normal',
  lightning: 'normal',
  poison: 'immune',
  spirit: 'immune',
};

const notOnWood = true;
const onlyOnFlat = true;
const notOnFloor = true;
const onlyCeiling = true;
// const nonRepairable = true;
// const nonRemovable = true;
const allowedInDungeons = true;
const groundOnly = true;

const requiresRoof = true;
const requiresFire = true;
const providesSupport = true;

type StructuralIntegrity = {
  maxSupport: number;
  minSupport: number;
  verticalLoss: number;
  horizontalLoss: number;
};

const durabilityTable: Record<MaterialType, StructuralIntegrity> = {
  [MaterialType.Wood]: {
    maxSupport: 100,
    minSupport: 10,
    verticalLoss: 1/8,
    horizontalLoss: 1/5,
  },
  [MaterialType.HardWood]: {
    maxSupport: 140,
    minSupport: 10,
    verticalLoss: 1/10,
    horizontalLoss: 1/6,
  },
  [MaterialType.Stone]: {
    maxSupport: 1000,
    minSupport: 100,
    verticalLoss: 1/8,
    horizontalLoss: 1,
  },
  [MaterialType.Iron]: {
    maxSupport: 1500,
    minSupport: 20,
    verticalLoss: 1/13,
    horizontalLoss: 1/13,
  },
  [MaterialType.Marble]: {
    maxSupport: 1500,
    minSupport: 100,
    verticalLoss: 1/8,
    horizontalLoss: 1/2,
  },
  [MaterialType.Ashstone]: {
    maxSupport: 2000,
    minSupport: 100,
    verticalLoss: 1/10,
    horizontalLoss: 1/3,
  },
  [MaterialType.Ancient]: {
    maxSupport: 5000,
    minSupport: 100,
    verticalLoss: 1/15,
    horizontalLoss: 1/4,
  },
};

export function getStructuralIntegrity(material: MaterialType): StructuralIntegrity {
  return durabilityTable[material];
}

const stoneStructure = (
  id: string,
  size: [width: number, depth: number, height: number],
  stone: number,
): Piece => ({
  id,
  base: false,
  burnable: true,
  type: 'piece',
  subtype: 'structure',
  tier: 3,
  piece: { target: 'random', water: undefined, size },
  wear: stoneStructureWear,
  recipe: stoneStructureRecipe(stone),
});

const graustenStructure = (
  id: string,
  hp: number,
  size: [width: number, depth: number, height: number],
  stone: number,
): Piece => ({
  id,
  base: false,
  type: 'piece',
  subtype: 'structure',
  tier: 7,
  piece: { target: 'random', water: undefined, size },
  wear: graustenStructureWear(hp),
  recipe: graustenStructureRecipe(stone),
});

const marbleStructure = (
  id: string,
  size: [width: number, depth: number, height: number],
  marble: number,
  { health = 1500, resist = marbleResist, iconId }: { health?: number, resist?: DamageModifiers, iconId?: string } = {},
): Piece => ({
  id,
  iconId,
  base: false,
  burnable: true,
  type: 'piece',
  subtype: 'structure',
  tier: 6,
  piece: { target: 'random', water: undefined, size },
  wear: wearStructure(health, resist, MaterialType.Marble, { noRoof: false, ashResist: true, burnable: false }),
  recipe: {
    type: 'craft_piece',
    materials: { BlackMarble: marble },
    station: 'piece_stonecutter',
  },
});

const ashwoodStructure = (
  id: string,
  size: [width: number, depth: number, height: number],
  Blackwood: number,
  { health = 400, resist = ashwoodResist, iconId }: { health?: number, resist?: DamageModifiers, iconId?: string } = {},
): Piece => ({
  id,
  iconId,
  base: false,
  burnable: false,
  type: 'piece',
  subtype: 'structure',
  tier: 7,
  piece: { target: 'random', water: undefined, size },
  wear: wearStructure(health, resist, MaterialType.Wood, { noRoof: false }),
  recipe: ashwoodStructureRecipe(Blackwood),
});

export const pieces: Piece[] = [
  {
    id: 'fire_pit',
    base: true,
    burnable: false,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'fire',
    tier: 0,
    emoji: '🔥',
    piece: { target: 'random', water: false, notOnWood, onlyOnFlat, allowedInDungeons, size: [1.4, 0.2, 1.4] },
    comfort: { value: 1, group: 'fire', },
    fireplace: {
      fuel: 'Wood',
      capacity: 10,
      burnTime: 5000,
      minHeightAbove: 0.5,
      warmRadius: 8,
      lightRadius: 8,
      smoke: true,
      fireworks: true,
      ignite: { interval: 5, chance: 0.5, spread: 2, capsuleRadius: 0.45 },
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifiersFireplace,
      noRoof: false,
      noSupport: true,
      burnable: false,
      providesSupport,
      materialType: MaterialType.Wood,
    },
    Aoe: { damage: dmg({ fire: 10 }), self: 10, backstabBonus: 4 },
    PointLight: { color: '#FF8152', range: 10, intensity: 2 },
    recipe: {
      type: 'craft_piece',
      materials: { Wood: 5, Stone: 2 },
      station: null,
    }
  },
  {
    id: 'fire_pit_iron',
    base: true,
    burnable: false,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'fire',
    tier: 2,
    emoji: '🔥',
    piece: { target: 'random', water: false, onlyOnFlat, allowedInDungeons, size: [1.4, 1.2, 1.4] },
    comfort: { value: 1, group: 'fire' },
    fireplace: {
      fuel: 'Wood',
      capacity: 10,
      burnTime: 5000,
      minHeightAbove: 0.5,
      warmRadius: 4,
      lightRadius: 8,
      smoke: true,
      fireworks: true,
      ignite: { interval: 5, chance: 0.5, spread: 4, capsuleRadius: 0.67 },
    },
    wear: {
      hp: 200,
      damageModifiers: damageModifiersFireplace,
      noRoof: false,
      noSupport: true,
      burnable: false,
      materialType: MaterialType.Wood,
    },
    Aoe: { damage: dmg({ fire: 10 }), self: 10, backstabBonus: 4 }, // radius: 4
    recipe: {
      type: 'craft_piece',
      materials: { Ironpit: 1, Wood: 1 },
      station: null,
    }
  },
  {
    id: 'bonfire',
    base: true,
    burnable: false,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'fire',
    tier: 2,
    emoji: '🔥',
    piece: { target: 'random', water: false, notOnWood, onlyOnFlat, size: [4.3, 1, 4.3] },
    wear: {
      hp: 300,
      damageModifiers: damageModifiersFireplace,
      noRoof: false,
      noSupport: true,
      burnable: false,
      materialType: MaterialType.Wood,
    },
    Aoe: { damage: dmg({ fire: 20 }), self: 10, backstabBonus: 4 },
    PointLight: { color: '#FF8152', range: 20, intensity: 2 },
    comfort: { value: 1, group: 'fire' },
    fireplace: {
      fuel: 'Wood',
      capacity: 10,
      burnTime: 5000,
      minHeightAbove: 1,
      warmRadius: 15,
      lightRadius: 15,
      smoke: true,
      fireworks: true,
      ignite: { interval: 5, chance: 0.75, spread: 4, capsuleRadius: 1.69 },
    },
    recipe: {
      type: 'craft_piece',
      materials: { 
        SurtlingCore: 1,
        ElderBark: 5,
        RoundLog: 5,
        FineWood: 5,
      },
      station: 'piece_workbench',
    }
  },
  {
    id: 'hearth',
    base: true,
    burnable: false,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'fire',
    tier: 3,
    emoji: '🔥',
    piece: { target: 'primary', water: false, size: [4, 0.6, 3] },
    comfort: { value: 2, group: 'fire' },
    fireplace: {
      fuel: 'Wood',
      capacity: 20,
      burnTime: 5000,
      minHeightAbove: 0.7,
      warmRadius: 13,
      lightRadius: 13,
      smoke: true,
      fireworks: false,
      ignite: { interval: 5, chance: 0.5, spread: 4, capsuleRadius: 0.79 },
    },
    wear: {
      hp: 1500,
      damageModifiers: craftStationResist,
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Stone,
    },
    Aoe: { damage: dmg({ fire: 10 }), self: 10, backstabBonus: 4 },
    PointLight: { color: '#FF9E7B', range: 13, intensity: 2 },
    recipe: stoneStructureRecipe(15),
  },
  {
    id: 'wood_stack',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 0,
    emoji: '🪵',
    wear: {
      hp: 50,
      damageModifiers: damageModifiersWood,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [2.3, 1.3, 2.4] },
    recipe: { type: 'craft_piece', materials: { Wood: 50, }, station: 'piece_workbench', }
  },
  {
    id: 'wood_fine_stack',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 2,
    emoji: '🪵',
    wear: {
      hp: 10,
      damageModifiers: damageModifiersWood,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [1.9, 0.9, 1.9] },
    recipe: { type: 'craft_piece', materials: { FineWood: 50, }, station: null, }
  },
  {
    id: 'wood_core_stack',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 1,
    emoji: '🪵',
    wear: {
      hp: 10,
      damageModifiers: damageModifiersWood,
      noRoof: true,
      noSupport: true,
    },
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [2, 1.3, 2.2] },
    recipe: { type: 'craft_piece', materials: { RoundLog: 50, }, station: null, }
  },
  {
    id: 'wood_yggdrasil_stack',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 1,
    emoji: '🪵',
    wear: {
      hp: 50,
      damageModifiers: damageModifiersWood,
      noRoof: true,
      noSupport: true,
    },
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [2, 0.9, 1] },
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 50, }, station: null, }
  },
  {
    id: 'blackwood_stack',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 7,
    emoji: '🪵',
    wear: {
      hp: 10,
      damageModifiers: { ...damageModifiersWood, fire: 'resistant' },
      noRoof: true,
      noSupport: true,
    },
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [2, 0.9, 1] },
    recipe: { type: 'craft_piece', materials: { Blackwood: 50 }, station: null, }
  },
  {
    id: 'bone_stack',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 1,
    emoji: '🦴',
    wear: {
      hp: 10,
      damageModifiers: { ...damageModifiersWood },
      noRoof: true,
      noSupport: true,
      burnable: false,
    },
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [0.9, 0.5, 0.9] },
    recipe: { type: 'craft_piece', materials: { BoneFragments: 50 }, station: null, }
  },
  {
    id: 'stone_pile',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 0,
    emoji: '🪨',
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [1.7, 0.9, 1.5] },
    wear: {
      hp: 50,
      damageModifiers: mods([0, 0, 0, 0, 2, 1, 1, 0, 3, 3]),
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Stone: 50 }, station: null, }
  },
  {
    id: 'coal_pile',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 1,
    emoji: '◼',
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [1.5, 1, 1.5] },
    wear: {
      hp: 50,
      damageModifiers: mods([0, 0, 0, 0, 2, 2, 1, 0, 3, 3]),
      noRoof: false,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Coal: 50 }, station: null, }
  },
  {
    id: 'blackmarble_pile',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 6,
    emoji: '⬛',
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [1.6, 1, 1.6] },
    wear: {
      hp: 100,
      damageModifiers: mods([0, 0, 0, 0, 2, 1, 1, 0, 3, 3]),
      noRoof: false,
      noSupport: true,
      ashResist: true,
    },
    recipe: { type: 'craft_piece', materials: { BlackMarble: 50 }, station: null, }
  },
  {
    id: 'grausten_pile',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 7,
    emoji: '⬜',
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [2.1, 1.5, 2.3] },
    wear: {
      hp: 100,
      damageModifiers: mods([0, 0, 0, 0, 2, 1, 1, 0, 3, 3]),
      noRoof: false,
      noSupport: true,
      ashResist: true,
      burnable: false,
    },
    recipe: { type: 'craft_piece', materials: { Grausten: 50 }, station: null, }
  },
  {
    id: 'skull_pile',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 7,
    emoji: '💀',
    piece: { target: 'random', water: undefined, allowedInDungeons, size: [1.6, 1, 1.6] },
    wear: {
      hp: 50,
      damageModifiers: mods([0, 0, 0, 0, 2, 1, 0, 0, 4, 4]),
      noRoof: false,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Charredskull: 50 }, station: null, }
  },
  {
    id: 'piece_cookingstation',
    base: false,
    burnable: false,
    type: 'piece',
    components: ['CookingStation'],
    subtype: 'craft',
    group: 'cook',
    tier: 1,
    emoji: '🍳',
    craft: {
      skill: SkillType.Cooking,
      requiresFire,
      batchSize: 2,
    },
    piece: { target: 'primary', water: false, allowedInDungeons, size: [0.4, 1, 1.5] },
    wear: {
      hp: 20,
      damageModifiers: cookingResist,
      noRoof: false,
      noSupport: true,
      burnable: false,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Wood: 2, }, station: 'piece_workbench', }
  },
  {
    id: 'piece_cookingstation_iron',
    base: false,
    burnable: false,
    type: 'piece',
    components: ['CookingStation'],
    subtype: 'craft',
    group: 'cook',
    tier: 3,
    emoji: '🍳',
    craft: {
      requiresFire,
      batchSize: 5,
    },
    piece: { target: 'primary', water: false, allowedInDungeons, size: [0.7, 2, 3.6] },
    wear: {
      hp: 20,
      damageModifiers: mods([0, 0, 1, 0, 1, 3, 0, 0, 3, 3]),
      noRoof: false,
      noSupport: true,
      burnable: false,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 3, Chain: 3 }, station: 'forge', }
  },
  {
    id: 'piece_cauldron',
    base: false,
    burnable: false,
    type: 'piece',
    components: ['CraftingStation'],
    subtype: 'craft',
    group: 'cook',
    tier: 2,
    emoji: '🍳',
    piece: { target: 'primary', water: false, size: [1, 1.4, 1] },
    craft: {
      skill: SkillType.Cooking,
      requiresFire,
    },
    wear: {
      hp: 100,
      damageModifiers: cookingResist,
      noRoof: false,
      noSupport: true,
      burnable: false,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Tin: 10, }, station: 'forge', }
  },
  {
    id: 'cauldron_ext1_spice',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 3,
    emoji: '🧰',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [2.2, 2.3, 0.5] },
    extends: {
      id: 'piece_cauldron',
      distance: 5,
    },
    wear: {
      hp: 50,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Dandelion: 3, Carrot: 2, Mushroom: 5, Thistle: 3, Turnip: 3 }, station: 'piece_workbench', }
  },
  {
    id: 'cauldron_ext3_butchertable',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 4,
    emoji: '🧰',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2.5, size: [1.2, 1.4, 1.2] },
    extends: {
      id: 'piece_cauldron',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifiersWood,
      noRoof: true,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { ElderBark: 2, RoundLog: 4, FineWood: 4, Silver: 2 }, station: 'piece_workbench', }
  },
  {
    id: 'cauldron_ext4_pots',
    base: false,
    type: 'piece',
    subtype: 'craft_ext',
    components: ['CraftingStationExtension'],
    tier: 5,
    emoji: '🧰',
    piece: { target: 'primary', water: false, notOnFloor, requiredSpace: 2.5, size: [2, 2.1, 0.7] },
    extends: {
      id: 'piece_cauldron',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifiersWood,
      noRoof: true,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 5, Copper: 5, BlackMetal: 5, FineWood: 10 }, station: 'piece_workbench', }
  },
  {
    id: 'cauldron_ext5_mortarandpestle',
    base: false,
    type: 'piece',
    subtype: 'craft_ext',
    components: ['CraftingStationExtension'],
    tier: 6,
    emoji: '🧰',
    piece: { target: 'primary', water: false, requiredSpace: 2.5, size: [2, 1, 0.9] },
    extends: {
      id: 'piece_cauldron',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifiersWood,
      noRoof: true,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { BlackMarble: 8, FineWood: 6, RoundLog: 4 }, station: 'piece_workbench', }
  },
  {
    id: 'cauldron_ext6_rollingpins',
    base: false,
    type: 'piece',
    subtype: 'craft_ext',
    components: ['CraftingStationExtension'],
    tier: 7,
    emoji: '🧰',
    piece: { target: 'primary', water: false, requiredSpace: 2.5, size: [1, 1, 1] },
    extends: {
      id: 'piece_cauldron',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifiersWood,
      noRoof: true,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Blackwood: 8, FineWood: 6, FlametalNew: 4 }, station: 'piece_workbench', }
  },
  {
    id: 'piece_preptable',
    base: false,
    type: 'piece',
    subtype: 'craft',
    craft: { skill: SkillType.Cooking, requiresRoof },
    group: 'cook',
    components: ['CraftingStation'],
    tier: 3,
    emoji: '',
    piece: { target: 'primary', water: false, size: [0.7, 1.2, 2.5] },
    wear: {
      hp: 100,
      damageModifiers: cookingResist,
      noRoof: true,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 5, FineWood: 20, LeatherScraps: 15 }, station: 'piece_workbench', }
  },
  {
    id: 'piece_MeadCauldron',
    base: false,
    type: 'piece',
    subtype: 'craft',
    craft: { skill: SkillType.Cooking, requiresFire },
    group: 'cook',
    components: ['CraftingStation'],
    tier: 2,
    emoji: '',
    piece: { target: 'primary', water: false, size: [0.8, 1.1, 1.3] },
    wear: {
      hp: 100,
      damageModifiers: cookingResist,
      noRoof: true,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Tin: 4, Copper: 6, LeatherScraps: 2 }, station: 'forge', }
  },
  {
    id: 'piece_oven',
    base: false,
    type: 'piece',
    components: ['CookingStation'],
    subtype: 'craft',
    group: 'cook',
    tier: 5,
    emoji: '🍳',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [2, 2.4, 3] },
    craft: {},
    wear: {
      hp: 500,
      damageModifiers: damageModifieresExtensions,
      noRoof: false,
      noSupport: true,
      burnable: false,
      materialType: MaterialType.Stone,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 15, Stone: 20, SurtlingCore: 4 }, station: 'piece_artisanstation', }
  },
  {
    id: 'piece_workbench',
    base: true,
    type: 'piece',
    group: 'craft_station',
    components: ['CraftingStation'],
    subtype: 'craft',
    tier: 1,
    emoji: '🧰',
    piece: { target: 'primary', water: false, size: [3, 1.8, 1.3] },
    craft: {
      skill: SkillType.Crafting,
      requiresRoof,
    },
    wear: {
      hp: 100,
      damageModifiers: mods([0, 0, 1, 2, 0, 0, 0, 0, 3, 3]),
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Wood: 10, }, station: null, }
  },
  {
    id: 'piece_workbench_ext1',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 1,
    emoji: '🧰',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [0.7, 1, 0.7] },
    extends: {
      id: 'piece_workbench',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Wood: 10, Flint: 10 }, station: 'piece_workbench', }
  },
  {
    id: 'piece_workbench_ext2',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 1,
    emoji: '🧰',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [1.6, 1.3, 0.8] },
    extends: {
      id: 'piece_workbench',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Wood: 10, Flint: 15, LeatherScraps: 20, DeerHide: 5, }, station: 'piece_workbench', }
  },
  {
    id: 'piece_workbench_ext3',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 2,
    emoji: '🧰',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [2.1, 0.45, 1.1] },
    extends: {
      id: 'piece_workbench',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { FineWood: 10, Bronze: 3, }, station: 'forge', }
  },
  {
    id: 'piece_workbench_ext4',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 4,
    emoji: '🧰',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [1.5, 1.3, 0.3] },
    extends: {
      id: 'piece_workbench',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { FineWood: 10, Iron: 4, Obsidian: 4, }, station: 'forge', }
  },
  {
    id: 'piece_stonecutter',
    base: true,
    type: 'piece',
    components: ['CraftingStation'],
    group: 'craft_station',
    subtype: 'craft',
    tier: 3,
    emoji: '⚒️',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [3.3, 2.1, 1.6] },
    craft: {
      skill: SkillType.Crafting,
      buildRange: 20,
      requiresRoof,
    },
    wear: {
      hp: 200,
      damageModifiers: mods([0, 0, 1, 2, 0, 0, 0, 0, 3, 3]),
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Wood: 10, Iron: 2, Stone: 4 }, station: 'forge', }
  },
  {
    id: 'piece_artisanstation',
    base: true,
    type: 'piece',
    components: ['CraftingStation'],
    group: 'craft_station',
    subtype: 'craft',
    tier: 5,
    emoji: '🔬',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [2.8, 2.6, 1.9] },
    craft: {
      skill: SkillType.Crafting,
      buildRange: 40,
      requiresRoof,
    },
    wear: {
      hp: 200,
      damageModifiers: mods([0, 0, 1, 2, 0, 0, 0, 0, 3, 3]),
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    PointLight: { color: '#87DCFF', range: 3, intensity: 1.5 },
    recipe: { type: 'craft_piece', materials: { FineWood: 10, DragonTear: 2, }, station: 'piece_workbench', }
  },
  {
    id: 'artisan_ext1',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 3,
    emoji: '🔬',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [1, 1, 1] },
    extends: { id: 'piece_artisanstation', distance: 5 },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { BlackMarble: 5, Bronze: 5, QueenDrop: 5 }, station: 'piece_artisanstation', }
  },
  {
    id: 'forge',
    base: true,
    // warmRadius: 4.5,
    // lightRadius: 4.5,
    type: 'piece',
    components: ['CraftingStation'],
    group: 'craft_station',
    subtype: 'craft',
    tier: 2,
    emoji: '🛠️',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [1.8, 0.9, 1] },
    craft: {
      skill: SkillType.Crafting,
      buildRange: 20,
      requiresRoof,
    },
    wear: {
      hp: 200,
      damageModifiers: mods([0, 0, 1, 2, 0, 0, 0, 0, 3, 3]),
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Stone: 4, Coal: 4, Wood: 10, Copper: 6 }, station: 'piece_workbench', }
  },
  {
    id: 'forge_ext5',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 3,
    emoji: '🛠️',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [1, 1, 1] },
    extends: { id: 'forge', distance: 5 },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { FineWood: 25, Copper: 10, }, station: 'piece_workbench', }
  },
  {
    id: 'forge_ext2',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 2,
    emoji: '🛠️',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [0.8, 0.8, 0.9] },
    extends: { id: 'forge', distance: 5 },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Wood: 5, Bronze: 2, }, station: 'piece_workbench', }
  },
  {
    id: 'forge_ext4',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 3,
    emoji: '🛠️',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [0.9, 1.4, 0.9] },
    extends: { id: 'forge', distance: 5 },
    wear: {
      hp: 1000,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 20, Wood: 5, }, station: 'piece_workbench', }
  },
  {
    id: 'forge_ext6',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 4,
    emoji: '🛠️',
    piece: { target: 'primary', water: false, notOnFloor, requiredSpace: 2, size: [2.5, 0.8, 0.4] },
    extends: { id: 'forge', distance: 5, },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 15, Wood: 10, }, station: 'piece_workbench', }
  },
  {
    id: 'forge_ext1',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 2,
    emoji: '🛠️',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [1.8, 1.9, 1.6] },
    extends: { id: 'forge', distance: 2 },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Wood: 5, DeerHide: 5, Chain: 4 }, station: 'piece_workbench', }
  },
  {
    id: 'forge_ext3',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 2,
    emoji: '🛠️',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [0.75, 1.4, 1] },
    extends: { id: 'forge', distance: 5 },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Wood: 25, SharpeningStone: 1, }, station: 'piece_workbench', }
  },
  {
    id: 'smelter',
    base: true,
    type: 'piece',
    components: ['Smelter'],
    subtype: 'craft',
    group: 'smelt',
    tier: 2,
    emoji: '🔥',
    piece: { target: 'primary', water: false, notOnWood, onlyOnFlat, size: [3, 4.25, 2.6] },
    // warmRadius: 4.5,
    // lightRadius: 4.5,
    craft: { queueSize: 10 },
    wear: {
      hp: 2000,
      damageModifiers: craftStationResist,
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Stone,
    },
    recipe: { type: 'craft_piece', materials: { Stone: 20, SurtlingCore: 5, }, station: 'piece_workbench', }
  },
  {
    id: 'blastfurnace',
    base: true,
    type: 'piece',
    subtype: 'craft',
    components: ['Smelter'],
    group: 'smelt',
    tier: 5,
    emoji: '🔥',
    piece: { target: 'primary', water: false, notOnWood, onlyOnFlat, size: [3.75, 5.4, 3.9] },
    craft: { queueSize: 10 },
    wear: {
      hp: 2000,
      damageModifiers: craftStationResist,
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Stone,
    },
    recipe: { type: 'craft_piece', materials: { Stone: 20, SurtlingCore: 5, Iron: 10, FineWood: 20 }, station: 'piece_artisanstation', }
  },
  {
    id: 'eitrrefinery',
    base: true,
    type: 'piece',
    subtype: 'craft',
    components: ['Smelter'],
    tier: 6,
    emoji: '⚗',
    piece: { target: 'primary', water: false, notOnWood, onlyOnFlat, size: [5.5, 4.3, 2.9] },
    craft: { queueSize: 20 },
    wear: {
      hp: 1000,
      damageModifiers: marbleResist,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Stone,
    },
    recipe: {
      type: 'craft_piece',
      materials: { BlackMarble: 20, BlackMetal: 5, YggdrasilWood: 10, BlackCore: 5, Sap: 3 },
      station: 'piece_artisanstation',
    },
  },
  {
    id: 'blackforge',
    base: true,
    type: 'piece',
    components: ['CraftingStation'],
    group: 'craft_station',
    subtype: 'craft',
    tier: 6,
    emoji: '🛠️',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [3.8, 1.1, 1.9] },
    craft: { skill: SkillType.Crafting, requiresRoof },
    wear: {
      hp: 200,
      damageModifiers: mods([0, 0, 1, 2, 0, 0, 0, 0, 3, 3]),
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { BlackMarble: 10, YggdrasilWood: 10, BlackCore: 5 }, station: 'piece_workbench', }
  },
  {
    id: 'blackforge_ext1',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 6,
    emoji: '🧰',
    piece: { target: 'primary', water: false, onlyOnFlat, requiredSpace: 2, size: [1.6, 0.33, 0.5] },
    extends: { id: 'blackforge', distance: 5 },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 5, Copper: 5, BlackMarble: 4 }, station: 'blackforge' }
  },
  {
    id: 'blackforge_ext2_vise',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 6,
    emoji: '🧰',
    piece: {
      target: 'primary',
      water: false,
      onlyOnFlat,
      requiredSpace: 2,
      size: [0.7, 1.2, 0.5],
    },
    extends: {
      id: 'blackforge',
      distance: 4,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: undefined,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 5, Copper: 8, MechanicalSpring: 2 }, station: 'blackforge' }
  },
  {
    id: 'blackforge_ext3_metalcutter',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 7,
    emoji: '🧰',
    piece: {
      target: 'primary',
      water: false,
      onlyOnFlat,
      requiredSpace: 2,
      size: [0.4, 1.2, 0.7],
    },
    extends: {
      id: 'blackforge',
      distance: 4,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { BlackMarble: 5, FlametalNew: 5, Blackwood: 5 }, station: 'blackforge' }
  },
  {
    id: 'blackforge_ext4_gemcutter',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 7,
    emoji: '🧰',
    piece: {
    target: 'primary',
      water: false,
      onlyOnFlat,
      requiredSpace: 2,
      size: [1.2, 0.7, 0.5],
    },
    extends: {
      id: 'blackforge',
      distance: 4,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    recipe: { type: 'craft_piece', materials: { FlametalNew: 5, Blackwood: 8, MorgenSinew: 2 }, station: 'blackforge' }
  },
  {
    id: 'charcoal_kiln',
    base: true,
    type: 'piece',
    components: ['Smelter'],
    subtype: 'craft',
    group: 'smelt',
    tier: 2,
    emoji: '🔥',
    piece: { target: 'primary', water: false, notOnWood, onlyOnFlat, size: [3.9, 3.1, 4] },
    craft: {
      queueSize: 25,
    },
    wear: {
      hp: 1500,
      damageModifiers: craftStationResist,
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Stone,
    },
    recipe: { type: 'craft_piece', materials: { Stone: 20, SurtlingCore: 5, }, station: 'piece_workbench', }
  },
  {
    id: 'windmill',
    base: true,
    type: 'piece',
    components: ['Smelter', 'Windmill'],
    subtype: 'craft',
    tier: 5,
    emoji: '🔥',
    piece: { target: 'primary', water: false, notOnWood, onlyOnFlat, size: [5, 8, 5] },
    craft: { queueSize: 50 },
    Aoe: { damage: dmg({ blunt: 20 }), self: 0 },
    wear: {
      hp: 1000,
      damageModifiers: craftStationResist,
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Stone,
    },
    recipe: { type: 'craft_piece', materials: { Stone: 20, Wood: 30, IronNails: 30 }, station: 'piece_artisanstation', }
  },
  {
    id: 'piece_spinningwheel',
    base: true,
    type: 'piece',
    components: ['Smelter'],
    subtype: 'craft',
    tier: 5,
    piece: { target: 'primary', water: false, onlyOnFlat, size: [3, 2.2, 1.9] },
    craft: { queueSize: 40, requiresRoof },
    wear: {
      hp: 200,
      damageModifiers: craftStationResist,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Stone,
    },
    recipe: { type: 'craft_piece', materials: { FineWood: 20, IronNails: 10, LeatherScraps: 5 }, station: 'piece_artisanstation', }
  },
  // walls / floors
  {
    id: 'wood_floor_1x1',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'none', water: undefined, size: [1, 1, 0.1] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(1),
  },
  {
    id: 'wood_floor',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'none', water: undefined, size: [2, 2, 0.1] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_stair',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    emoji: '🪜',
    piece: { target: 'none', water: undefined, size: [2, 1, 2] },
    wear: {
      hp: 1000,
      damageModifiers: woodResist,
      materialType: MaterialType.HardWood, 
    },
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_stepladder',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    emoji: '🪜',
    piece: { target: 'none', water: undefined, size: [1, 2, 2] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_wall_quarter',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [1, 1, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(1),
  },
  {
    id: 'wood_wall_half',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(1),
  },
  {
    id: 'woodwall',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_wall_roof',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.4] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_wall_roof_upsidedown',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.4] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_wall_roof_top',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.4] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_wall_roof_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_wall_roof_45_upsidedown',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_wall_roof_top_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_roof',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 2] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_roof_top',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 0.8, 2] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_roof_ocorner',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 2] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_roof_icorner',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 2] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_roof_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 2, 2] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_roof_top_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 2, 2] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_roof_ocorner_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 2, 2] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_roof_icorner_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 2, 2] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_pole',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [0.4, 1, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(1),
  },
  {
    id: 'wood_pole2',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [0.4, 2, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_beam_1',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [1, 0.4, 0.4] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(1),
  },
  {
    id: 'wood_beam',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 0.4, 0.4] },
    wear: { ...woodStructureWear, damageModifiers: stoneResist },
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_beam_26',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.4] },
    wear: { ...woodStructureWear, damageModifiers: stoneResist },
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_beam_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.4] },
    wear: { ...woodStructureWear, damageModifiers: stoneResist },
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'wood_dragon1',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    piece: { target: 'random', water: undefined, size: [4, 1.85, 0.7] },
    wear: woodRoofStructureWear,
    recipe: woodStructureRecipe(10, 'FineWood'),
  },
  {
    id: 'wood_door',
    base: false,
    type: 'piece',
    components: ['Door'],
    subtype: 'structure',
    tier: 1,
    emoji: '🚪',
    piece: { target: 'random', water: undefined, size: [2, 2, 0.5] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(4),
  },
  {
    id: 'wood_gate',
    base: false,
    type: 'piece',
    components: ['Door'],
    subtype: 'structure',
    tier: 1,
    emoji: '🚪',
    piece: { target: 'random', water: undefined, size: [2, 0.5, 3] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(12),
  },
  {
    id: 'darkwood_gate',
    base: false,
    type: 'piece',
    components: ['Door'],
    subtype: 'structure',
    tier: 5,
    emoji: '🚪',
    piece: { target: 'random', water: undefined, size: [2, 4, 0.4] },
    wear: woodStructureWear,
    recipe: { type: 'craft_piece', materials: { Wood: 16, Iron: 4, Tar: 2 }, station: 'forge', },
  },
  {
    id: 'ashwood_door',
    base: false,
    type: 'piece',
    components: ['Door'],
    subtype: 'structure',
    tier: 7,
    emoji: '🚪',
    piece: { target: 'random', water: undefined, size: [2, 3, 0.15] },
    wear: ironStructureWear, // ?
    recipe: { type: 'craft_piece', materials: { FlametalNew: 1, Blackwood: 3 }, station: 'piece_workbench', },
  },
  {
    id: 'flametal_gate',
    base: false,
    type: 'piece',
    components: ['Door'],
    subtype: 'structure',
    tier: 7,
    emoji: '🚪',
    piece: { target: 'random', water: undefined, size: [3, 6.5, 0.4] },
    wear: { ...ironStructureWear, ashResist: true, burnable: false },
    recipe: { type: 'craft_piece', materials: { FlametalNew: 16 }, station: 'forge', },
  },
  {
    id: 'wood_window',
    base: false,
    type: 'piece',
    components: ['Door'],
    subtype: 'structure',
    tier: 1,
    emoji: '🚪',
    piece: { target: 'random', water: undefined, size: [1, 0.2, 1] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(4),
  },
  {
    id: 'piece_hexagonal_door',
    base: false,
    type: 'piece',
    components: ['Door'],
    subtype: 'structure',
    tier: 1,
    emoji: '🚪',
    piece: { target: 'random', water: undefined, size: [4, 0.2, 4] },
    wear: wearStructure(1500, woodResist, MaterialType.HardWood),
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 8, Copper: 8 }, station: 'blackforge', },
  },
  {
    id: 'wood_pole_log',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    piece: { target: 'random', water: undefined, size: [0.5, 2, 0.5] },
    wear: wearStructure(500, woodResist, MaterialType.HardWood),
    recipe: woodStructureRecipe(1, 'RoundLog'),
  },
  {
    id: 'wood_pole_log_4',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    piece: { target: 'random', water: undefined, size: [0.5, 4, 0.5] },
    wear: wearStructure(500, woodResist, MaterialType.HardWood),
    recipe: woodStructureRecipe(2, 'RoundLog'),
  },
  {
    id: 'wood_wall_log',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    piece: { target: 'random', water: undefined, size: [2, 0.5, 0.5] },
    wear: wearStructure(500, woodResist, MaterialType.HardWood),
    recipe: woodStructureRecipe(1, 'RoundLog'),
  },
  {
    id: 'wood_wall_log_4x0.5',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    piece: { target: 'random', water: undefined, size: [4, 0.5, 0.5] },
    wear: wearStructure(500, woodResist, MaterialType.HardWood),
    recipe: woodStructureRecipe(2, 'RoundLog'),
  },
  {
    id: 'wood_log_26',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.5] },
    wear: wearStructure(400, stoneResist, MaterialType.HardWood),
    recipe: woodStructureRecipe(2, 'RoundLog'),
  },
  {
    id: 'wood_log_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.5] },
    wear: wearStructure(400, stoneResist, MaterialType.HardWood),
    recipe: woodStructureRecipe(2, 'RoundLog'),
  },
  { 
    id: 'darkwood_roof',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 1, 2] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_roof_top',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 1, 2] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_roof_ocorner',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 1, 2] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_roof_icorner',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 1, 2] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_roof_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 2, 2] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_roof_top_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 1, 2] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_roof_ocorner_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 2, 2] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_roof_icorner_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 2, 2] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_pole',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [0.5, 2, 0.5] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_pole4',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [0.5, 4, 0.5] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 4, Tar: 1 }),
  },
  {
    id: 'darkwood_beam',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 0.5, 0.5] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_beam4',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [4, 0.5, 0.5] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 4, Tar: 1 }),
  },
  {
    id: 'darkwood_beam_26',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.5] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_beam_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.5] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_decowall',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [1, 2, 0.15] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ FineWood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_arch',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.5] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ Wood: 2, Tar: 1 }),
  },
  {
    id: 'darkwood_raven',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [3, 1.4, 0.5] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ FineWood: 10, Tar: 1 }),
  },
  {
    id: 'darkwood_wolf',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 5,
    piece: { target: 'random', water: undefined, size: [2.2, 2, 1] },
    wear: darkwoodStructureWear,
    recipe: darkwoodStructureRecipe({ FineWood: 10, Tar: 1 }),
  },
  {
    id: 'wood_fence',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2.7, 2.3, 0.3] },
    wear: woodStructureWear,
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'stake_wall',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 1,
    piece: { target: 'random', water: undefined, size: [2.1, 3.5, 0.6] },
    wear: wearStructure(1000, woodResist, MaterialType.Wood, { noRoof: false }),
    recipe: woodStructureRecipe(2),
  },
  {
    id: 'piece_sharpstakes',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    Aoe: { damage: dmg({ pierce: 15 }), self: 34 },
    piece: { target: 'random', water: undefined, groundOnly, size: [2.4, 1.5, 2.7] },
    wear: wearStructure(200, woodResist, MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Wood: 6, RoundLog: 4 }, station: 'piece_workbench', },
  },
  stoneStructure('stone_wall_1x1', [1, 1, 1], 3),
  stoneStructure('stone_wall_2x1', [2, 1, 1], 4),
  stoneStructure('stone_wall_4x2', [4, 2, 1], 6),
  stoneStructure('stone_pillar', [1, 2, 1], 5),
  stoneStructure('stone_arch', [2, 1, 1], 4),
  stoneStructure('stone_floor_2x2', [2, 1, 2], 6),
  { ...stoneStructure('stone_floor', [4, 1, 4], 8), disabled: true },
  stoneStructure('stone_stair', [2, 1, 2], 8),
  marbleStructure('blackmarble_1x1', [1, 1, 1], 2),
  marbleStructure('blackmarble_2x1x1', [2, 1, 1], 4),
  marbleStructure('blackmarble_2x2x2', [2, 2, 2], 8),
  marbleStructure('blackmarble_arch', [2, 2, 1], 5),
  marbleStructure('blackmarble_base_1', [2, 2, 2], 5),
  marbleStructure('blackmarble_basecorner', [2, 2, 2], 6),
  marbleStructure('blackmarble_column_1', [1, 1, 1], 2),
  marbleStructure('blackmarble_column_2', [2, 1, 2], 4),
  marbleStructure('blackmarble_floor', [2, 1, 2], 4),
  marbleStructure('blackmarble_floor_triangle', [2, 2, 1], 3, { resist: mods([1, 1, 1, 1, 0, 3, 3, 3, 3, 3]) }), // ashResist: false
  marbleStructure('blackmarble_out_1', [2, 2, 1], 5),
  marbleStructure('blackmarble_outcorner', [2, 2, 2], 6),
  marbleStructure('blackmarble_stair', [2, 1, 2], 8, { resist: mods([1, 1, 1, 1, 0, 3, 3, 3, 3, 3]) }),
  marbleStructure('blackmarble_tip', [1, 2, 1], 2), // ashResist: false
  {
    id: 'iron_floor_1x1',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [1, 0.1, 1] },
    wear: {
      hp: 500,
      damageModifiers: ironResist,
      materialType: MaterialType.Iron,
      ashResist: true,
      burnable: false,
    },
    recipe: ironStructureRecipe(1),
  },
  {
    id: 'iron_floor_2x2',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [2, 0.1, 2] },
    wear: {
      hp: 1000,
      damageModifiers: ironResist,
      materialType: MaterialType.Iron,
      ashResist: true,
      burnable: false,
    },
    recipe: ironStructureRecipe(2),
  },
  {
    id: 'iron_wall_1x1',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [1, 1, 0.1] },
    wear: {
      hp: 500,
      damageModifiers: ironResist,
      materialType: MaterialType.Iron,
      ashResist: true,
      burnable: false,
    },
    recipe: ironStructureRecipe(1),
  },
  {
    id: 'iron_wall_2x2',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.1] },
    wear: { ...ironStructureWear, ashResist: true, burnable: false, },
    recipe: ironStructureRecipe(2),
  },
  {
    id: 'piece_dvergr_metal_wall_2x2',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 6,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.1] },
    wear: { ...ironStructureWear, ashResist: true, burnable: false, },
    recipe: { type: 'craft_piece', materials: { Copper: 8 }, station: 'blackforge' },
  },
  {
    id: 'woodiron_pole',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [0.25, 2, 0.25] },
    wear: wearStructure(2000, woodResist, MaterialType.Iron),
    recipe: { type: 'craft_piece', materials: { Wood: 2, Iron: 1 }, station: 'forge', },
  },
  {
    id: 'woodiron_beam',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [2, 0.25, 0.25] },
    wear: wearStructure(2000, woodResist, MaterialType.Iron),
    recipe: { type: 'craft_piece', materials: { Wood: 2, Iron: 1 }, station: 'forge', },
  },
  {
    id: 'woodiron_beam_26',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [2, 1, 0.25] },
    wear: wearStructure(2000, woodResist, MaterialType.Iron),
    recipe: { type: 'craft_piece', materials: { Wood: 2, Iron: 1 }, station: 'forge', },
  },
  {
    id: 'woodiron_beam_45',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [2, 2, 0.25] },
    wear: wearStructure(2000, woodResist, MaterialType.Iron),
    recipe: { type: 'craft_piece', materials: { Wood: 2, Iron: 1 }, station: 'forge', },
  },
  {
    id: 'iron_grate', // yes, grate
    base: false,
    type: 'piece',
    components: ['Door'],
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [1.8, 2.8, 0.1] },
    wear: wearStructure(2000, mods([0, 0, 0, 0, 0, 0, 0, 0, 3, 3]), MaterialType.Iron, { ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Iron: 4 }, station: 'forge' },
  },
  {
    id: 'piece_trap_troll',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    piece: { target: 'random', water: undefined, groundOnly, allowedInDungeons, size: [2, 0.3, 2] },
    wear: wearStructure(300, ironResist, undefined, { noRoof: false, burnable: false }),
    Aoe: { damage: dmg({ blunt: 50, pierce: 50, chop: 50 }), self: 31, backstabBonus: 4 },
    recipe: { type: 'craft_piece', materials: { BlackMetal: 5, BronzeNails: 10, MechanicalSpring: 1 }, station: 'piece_workbench', },
  },
  {
    id: 'piece_turret',
    components: ['Turret'],
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 2,
    Turret: {
      attackCooldown: 2.5,
      allowedAmmo: ['TurretBoltWood', 'TurretBolt'],
    },
    piece: { target: 'random', water: undefined, groundOnly, allowedInDungeons, size: [1.5, 1.2, 1.3] },
    wear: wearStructure(250, ironResist, undefined),
    recipe: { type: 'craft_piece', materials: { BlackMetal: 20, MechanicalSpring: 3 }, station: 'piece_workbench', },
  },
  {
    id: 'bed',
    components: ['Bed'],
    group: 'bed', 
    base: true,
    type: 'piece',
    subtype: 'bed',
    tier: 1,
    emoji: '🛏️',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [3.1, 0.7, 1.7] },
    comfort: { value: 1, group: 'bed' },
    wear: wearStructure(100, woodResist, MaterialType.Wood),
    recipe: woodStructureRecipe(8),
  },
  {
    id: 'piece_bed02',
    components: ['Bed'],
    group: 'bed', 
    base: true,
    type: 'piece',
    subtype: 'bed',
    tier: 4,
    emoji: '🛏️',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [2.8, 1.45, 3.5] },
    comfort: { value: 2, group: 'bed' },
    wear: wearStructure(300, woodResist, MaterialType.Wood),
    recipe: {
      type: 'craft_piece',
      materials: { FineWood: 40, DeerHide: 7, WolfPelt: 4, Feathers: 10, IronNails: 15 },
      station: 'piece_workbench',
    },
  },
  {
    id: 'ashwood_bed',
    components: ['Bed'],
    group: 'bed', 
    base: true,
    burnable: false,
    type: 'piece',
    subtype: 'bed',
    tier: 7,
    emoji: '🛏️',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [3.1, 0.7, 1.7] },
    comfort: { value: 1, group: 'bed' },
    wear: wearStructure(100, ashwoodResist, MaterialType.Wood),
    recipe: {
      type: 'craft_piece',
      materials: { Blackwood: 8, LoxPelt: 2, AskHide: 2 },
      station: 'piece_workbench',
    },
  },
  {
    id: 'piece_chest_wood',
    group: 'chest',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 1,
    emoji: '📦',
    space: [5, 2],
    piece: { target: 'primary', water: false, onlyOnFlat, size: [1.8, 0.85, 0.85] },
    wear: wearStructure(100, chestResist, MaterialType.Wood, { noRoof: false }),
    recipe: woodStructureRecipe(10),
  },
  {
    id: 'piece_chest_barrel',
    group: 'chest',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 3,
    emoji: '🛢',
    space: [6, 2],
    piece: { target: 'primary', water: false, onlyOnFlat, size: [0.7, 0.9, 0.7] },
    wear: wearStructure(200, chestResist, undefined, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Wood: 10, BarrelRings: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_chest',
    group: 'chest', 
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 3,
    emoji: '📦',
    space: [6, 4],
    piece: { target: 'primary', water: false, onlyOnFlat, size: [2, 1.2, 1.05] },
    wear: wearStructure(1000, chestResist, MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 10, Iron: 2 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_chest_private',
    group: 'chest', 
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 3,
    space: [3, 2],
    piece: { target: 'primary', water: false, onlyOnFlat, size: [1, 0.6, 0.5] },
    wear: wearStructure(10000, chestResist, MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 10, Iron: 8 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_chest_blackmetal',
    group: 'chest', 
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 5,
    emoji: '📦',
    space: [8, 4],
    piece: { target: 'primary', water: false, onlyOnFlat, size: [2.4, 1, 1.5] },
    wear: wearStructure(1000, chestResist, MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Wood: 10, Tar: 2, BlackMetal: 6 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_chair',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    emoji: '🪑',
    tier: 2,
    comfort: { value: 1, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [0.8, 0.8, 0.7] },
    wear: wearStructure(100, woodResist, MaterialType.Wood),
    recipe: woodStructureRecipe(4, 'FineWood'),
  },
  {
    id: 'piece_chair02',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 2,
    emoji: '🪑',
    comfort: { value: 2, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [0.4, 1.2, 1.1] },
    wear: wearStructure(100, woodResist, MaterialType.Wood),
    recipe: woodStructureRecipe(4, 'FineWood'),
  },
  {
    id: 'piece_chair03',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 5,
    emoji: '🪑',
    comfort: { value: 2, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [0.6, 1.3, 0.6] },
    wear: wearStructure(200, woodResist, MaterialType.Wood),
    recipe: { type: 'craft_piece', materials: { FineWood: 4, Tar: 1, IronNails: 5, DeerHide: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_bench01',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 2,
    emoji: '🛋️',
    comfort: { value: 1, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [2.5, 0.5, 0.6] },
    wear: wearStructure(100, woodResist, MaterialType.Wood),
    recipe: woodStructureRecipe(4, 'FineWood'),
  },
  {
    id: 'piece_logbench01',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 3,
    emoji: '🪑',
    comfort: { value: 1, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, allowedInDungeons, size: [2.1, 0.5, 0.5] },
    wear: wearStructure(100, woodResist, MaterialType.Wood),
    recipe: woodStructureRecipe(2, 'RoundLog'),
  },
  {
    id: 'piece_blackmarble_bench',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 3,
    emoji: '🪑',
    comfort: { value: 1, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [2.5, 1, 0.6] },
    wear: wearStructure(200, woodResist, MaterialType.Marble, { burnable: false }),
    recipe: { type: 'craft_piece', materials: { BlackMarble: 6, Copper: 3 }, station: 'piece_stonecutter' },
  },
  {
    id: 'piece_blackwood_bench01',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 7,
    emoji: '🪑',
    comfort: { value: 1, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [2, 0.5, 0.8] },
    wear: wearStructure(200, woodResist, MaterialType.Wood, { ashResist: true, }),
    recipe: { type: 'craft_piece', materials: { Blackwood: 6 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_throne01',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 3,
    emoji: '🪑',
    comfort: { value: 3, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [2, 2.8, 1] },
    wear: wearStructure(250, woodResist, MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 20, IronNails: 10 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_throne02',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 3,
    emoji: '🪑',
    comfort: { value: 3, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [1.6, 3, 1.1] },
    wear: wearStructure(500, stoneResist, MaterialType.Stone, { noRoof: false, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Stone: 20, DeerHide: 2, WolfPelt: 2 }, station: 'piece_stonecutter' },
  },
  {
    id: 'piece_blackmarble_throne',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 6,
    emoji: '🪑',
    comfort: { value: 3, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [1.6, 3, 1.2] },
    wear: wearStructure(500, stoneResist, MaterialType.Marble, { burnable: false }),
    recipe: { type: 'craft_piece', materials: { BlackMarble: 20, ScaleHide: 4, DeerHide: 2, Copper: 5 }, station: 'piece_stonecutter' },
  },
  {
    id: 'piece_bone_throne',
    base: false,
    type: 'piece',
    components: ['Chair'],
    group: 'chair',
    subtype: 'chair',
    tier: 7,
    emoji: '🪑',
    comfort: { value: 3, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [1.5, 2.75, 1] },
    wear: wearStructure(500, stoneResist, MaterialType.Marble, { burnable: false }),
    recipe: { type: 'craft_piece', materials: { CharredBone: 15, FlametalNew: 4, Grausten: 20, Charredskull: 3 }, station: 'piece_stonecutter' },
  },
  {
    id: 'piece_table',
    base: false,
    type: 'piece',
    subtype: 'table',
    tier: 2,
    comfort: { value: 1, group: 'table' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [2.5, 0.8, 1.2] },
    wear: wearStructure(100, woodResist, MaterialType.Wood),
    recipe: woodStructureRecipe(6, 'FineWood'),
  },
  {
    id: 'piece_table_round',
    base: false,
    type: 'piece',
    subtype: 'table',
    tier: 5,
    comfort: { value: 2, group: 'table' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [2.5, 0.8, 2.5] },
    wear: wearStructure(150, woodResist, MaterialType.Wood),
    recipe: { type: 'craft_piece', materials: { FineWood: 10, Tar: 2, IronNails: 20 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_table_oak',
    base: false,
    type: 'piece',
    subtype: 'table',
    tier: 5,
    comfort: { value: 2, group: 'table' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [6.6, 0.8, 1.7] },
    wear: wearStructure(200, woodResist, MaterialType.Wood),
    recipe: { type: 'craft_piece', materials: { FineWood: 20, Tar: 2, IronNails: 20 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_blackmarble_table',
    base: false,
    type: 'piece',
    subtype: 'table',
    tier: 2,
    comfort: { value: 2, group: 'table' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [2.5, 0.8, 1.5] },
    wear: wearStructure(100, woodResist, MaterialType.Marble, { burnable: false }),
    recipe: { type: 'craft_piece', materials: { BlackMarble: 6, Copper: 3 }, station: 'piece_stonecutter' },
  },
  {
    id: 'piece_walltorch',
    base: true,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'torch',
    tier: 3,
    fireplace: {
      fuel: 'Resin',
      capacity: 6,
      burnTime: 20000,
      minHeightAbove: 0.68,
      warmRadius: 0,
      lightRadius: 6,
      smoke: false,
      fireworks: false,
      ignite: { interval: 0, chance: 0, spread: 4, capsuleRadius: 0 },
    },
    piece: { target: 'primary', water: undefined, onlyOnFlat, size: [0.3, 1, 0.45] },
    wear: wearStructure(100, mods([2, 0, 0, 0, 0, 0, 0, 0, 3, 3]), undefined, { noRoof: false }),
    PointLight: { color: '#FF9E7B', range: 12, intensity: 1.5 },
    recipe: { type: 'craft_piece', materials: { Wood: 2, Copper: 2, Resin: 2 }, station: 'forge' },
  },
  {
    id: 'piece_groundtorch',
    base: true,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'torch',
    tier: 3,
    fireplace: {
      fuel: 'Resin',
      capacity: 6,
      burnTime: 20000,
      minHeightAbove: 0.76,
      warmRadius: 0,
      lightRadius: 3,
      smoke: false,
      fireworks: false,
      ignite: { interval: 10, chance: 1, spread: 1, capsuleRadius: 0.1 },
    },
    // fire: 6
    piece: { target: 'primary', water: undefined, onlyOnFlat, size: [0.25, 0.25, 1.5] },
    wear: wearStructure(200, torchResist, undefined, { noRoof: false }),
    PointLight: { color: '#FF9E7B', range: 15, intensity: 1.5 },
    recipe: { type: 'craft_piece', materials: { Iron: 2, Resin: 2 }, station: 'forge' },
  },
  {
    id: 'piece_groundtorch_wood',
    base: true,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'torch',
    tier: 1,
    fireplace: {
      fuel: 'Resin',
      capacity: 4,
      burnTime: 10000,
      minHeightAbove: 0.76,
      warmRadius: 0,
      lightRadius: 3,
      smoke: false,
      fireworks: false,
      ignite: { interval: 10, chance: 1, spread: 1, capsuleRadius: 0.05 },
    },
    // fire: 3,
    piece: { target: 'primary', water: undefined, onlyOnFlat, size: [0.25, 0.25, 1.5] },
    wear: wearStructure(100, mods([0, 2, 1, 2, 0, 0, 0, 0, 3, 3]), undefined, { noRoof: false }),
    PointLight: { color: '#FF9E7B', range: 10, intensity: 1.5 },
    recipe: { type: 'craft_piece', materials: { Iron: 2, Resin: 2 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_groundtorch_green',
    base: true,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'torch',
    tier: 3,
    fireplace: {
      fuel: 'Guck',
      capacity: 6,
      burnTime: 20000,
      minHeightAbove: 0.76,
      warmRadius: 0,
      lightRadius: 3,
      smoke: false,
      fireworks: false,
      ignite: { interval: 10, chance: 1, spread: 1, capsuleRadius: 0.1 },
    },
    // fire: 3
    piece: { target: 'primary', water: undefined, onlyOnFlat, size: [0.25, 0.25, 1.5] },
    wear: wearStructure(200, torchResist, undefined, { noRoof: false }),
    PointLight: { color: '#A1FF7B', range: 10, intensity: 1.5 },
    recipe: { type: 'craft_piece', materials: { Iron: 2, Resin: 2 }, station: 'forge' },
  },
  {
    id: 'piece_groundtorch_blue',
    base: true,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'torch',
    tier: 3,
    fireplace: {
      fuel: 'GreydwarfEye',
      capacity: 6,
      burnTime: 20000,
      minHeightAbove: 0.76,
      warmRadius: 0,
      lightRadius: 3,
      smoke: false,
      fireworks: false,
      ignite: { interval: 10, chance: 1, spread: 1, capsuleRadius: 0.1 },
    },
    // fire: 3
    piece: { target: 'primary', water: undefined, onlyOnFlat, size: [0.25, 0.25, 1.5] },
    wear: wearStructure(200, torchResist, undefined, { noRoof: false }),
    PointLight: { color: '#7BC5FF', range: 10, intensity: 1.5 },
    recipe: { type: 'craft_piece', materials: { Iron: 2, GreydwarfEye: 2 }, station: 'forge' },
  },
  {
    id: 'piece_groundtorch_mist',
    base: false,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'structure',
    group: 'torch',
    tier: 6,
    demister: 12,
    piece: { target: 'random', water: undefined, onlyOnFlat, allowedInDungeons, size: [0.1, 0.1, 1.4] },
    wear: wearStructure(200, torchResist, undefined, { noRoof: false }),
    PointLight: { color: '#7BC8FF', range: 10, intensity: 1 },
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 1, Wisp: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_brazierfloor01',
    base: true,
    burnable: false,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'torch',
    tier: 3,
    fireplace: {
      fuel: 'Coal',
      capacity: 5,
      burnTime: 20000,
      minHeightAbove: 0.28,
      warmRadius: 8,
      lightRadius: 8,
      smoke: false,
      fireworks: false,
      ignite: { interval: 5, chance: 0.5, spread: 1, capsuleRadius: 0.3 },
    },
    comfort: { value: 1, group: 'fire' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [1, 0.8, 1] },
    wear: wearStructure(100, torchResist, MaterialType.Wood, { noRoof: false, burnable: false }),
    Aoe: { damage: dmg({ fire: 10 }), self: 0, backstabBonus: 4 },
    recipe: { type: 'craft_piece', materials: { Bronze: 5, Coal: 2, WolfClaw: 3 }, station: 'forge' },
  },
  // piece_brazierfloor02
  {
    id: 'piece_brazierceiling01',
    base: true,
    burnable: false,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    group: 'torch',
    tier: 3,
    fireplace: {
      fuel: 'Coal',
      capacity: 5,
      burnTime: 20000,
      minHeightAbove: 0.28,
      warmRadius: 8,
      lightRadius: 8,
      smoke: false,
      fireworks: false,
      ignite: { interval: 5, chance: 0.5, spread: 1, capsuleRadius: 0.3 },
    },
    comfort: { value: 1, group: 'fire' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [1, 2, 1] },
    wear: wearStructure(100, torchResist, MaterialType.Wood, { noRoof: false, burnable: false }),
    Aoe: { damage: dmg({ fire: 10 }), self: 0, backstabBonus: 4 },
    recipe: { type: 'craft_piece', materials: { Bronze: 5, Coal: 2, Chain: 1 }, station: 'forge' },
  },
  {
    id: 'portal_wood',
    components: ['TeleportWorld'],
    base: true,
    type: 'piece',
    subtype: 'misc',
    tier: 2,
    PointLight: { color: '#FF6400', range: 10, intensity: 2 },
    piece: { target: 'primary', water: false, onlyOnFlat, size: [4.2, 3.3, 1.1] },
    wear: wearStructure(400, mods([1, 0, 1, 2, 1, 0, 0, 0, 3, 3]), MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { GreydwarfEye: 10, FineWood: 20, SurtlingCore: 2 }, station: 'piece_workbench' },
  },
  // portal: disabled
  {
    id: 'portal_stone',
    components: ['TeleportWorld'],
    base: true,
    type: 'piece',
    subtype: 'misc',
    tier: 7,
    // PointLight: { color: '#FF6400', range: 10, intensity: 2 },
    piece: { target: 'primary', water: false, onlyOnFlat, size: [8.6, 7, 3] },
    wear: wearStructure(1000, mods([1, 0, 1, 2, 1, 0, 0, 0, 3, 3]), MaterialType.Stone, { noRoof: false, ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { GreydwarfEye: 10, Grausten: 30, MoltenCore: 2 }, station: 'piece_stonecutter' },
  },
  {
    id: 'guard_stone',
    components: ['PrivateArea'],
    base: true,
    type: 'piece',
    subtype: 'misc',
    tier: 2,
    piece: { target: 'primary', water: false, onlyOnFlat, size: [1.7, 2.7, 1.3] },
    wear: wearStructure(1000, mods([0, 0, 1, 4, 4, 0, 1, 0, 3, 3]), undefined, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 5, GreydwarfEye: 5, SurtlingCore: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_asksvinskeleton',
    components: [],
    base: true,
    type: 'piece',
    subtype: 'misc',
    tier: 2,
    piece: { target: 'random', water: undefined, onlyOnFlat, size: [1.5, 2, 3] },
    wear: wearStructure(150, mods([0, 0, 1, 2, 0, 0, 0, 0, 3, 3]), undefined, { noRoof: false }),
    recipe: {
      type: 'craft_piece',
      materials: { BoneFragments: 50, AsksvinCarrionNeck: 1, AsksvinCarrionPelvic: 1, AsksvinCarrionRibcage: 1, AsksvinCarrionSkull: 1 },
      station: 'piece_workbench',
    }
  },
  // Cart, VikingShip, Raft, Karve 
  {
    id: 'itemstand',
    components: ['ItemStand'],
    base: false,
    type: 'piece',
    subtype: 'stand',
    group: 'stand',
    supportedTypes: [
      ItemType.Shield,
      ItemType.OneHandedWeapon,
      ItemType.Bow,
      ItemType.Helmet,
      ItemType.Tool,
      ItemType.Trophie,
      ItemType.TwoHandedWeapon,
      ItemType.Misc,
      ItemType.Fish,
      ItemType.TwoHandedWeaponLeft,
      ItemType.Ammo,
    ], // -MeadTasty
    // ['weapon', 'tool', 'shield', 'armor/head', 'trophy', 'resource']
    tier: 2,
    piece: { target: 'none', water: false, onlyOnFlat, size: [0.5, 0.5, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { FineWood: 4, BronzeNails: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'itemstandh',
    components: ['ItemStand'],
    base: false,
    type: 'piece',
    subtype: 'stand',
    group: 'stand',
    supportedTypes: [
      ItemType.Shield,
      ItemType.OneHandedWeapon,
      ItemType.Bow,
      ItemType.Helmet,
      ItemType.Tool,
      ItemType.Trophie,
      ItemType.TwoHandedWeapon,
      ItemType.Consumable,
      ItemType.Misc,
      ItemType.Fish,
      ItemType.TwoHandedWeaponLeft,
      ItemType.Ammo,
    ],
    // ['weapon', 'tool', 'shield', 'armor/head', 'trophy', 'resource']
    tier: 2,
    piece: { target: 'none', water: undefined, size: [0.2, 0.1, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { FineWood: 4, BronzeNails: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'sign',
    components: ['Sign'],
    base: false,
    type: 'piece',
    subtype: 'misc',
    tier: 2,
    piece: { target: 'none', water: undefined, allowedInDungeons, size: [1, 0.5, 0.1] },
    wear: { ...woodRoofStructureWear, hp: 100 },
    recipe: { type: 'craft_piece', materials: { Wood: 2, Coal: 1 }, station: null },
  },
  {
    id: 'rug_fur', // lox
    base: false,
    type: 'piece',
    subtype: 'decoration',
    group: 'rug',
    tier: 5,
    comfort: { value: 1, group: 'carpet' },
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [4, 0, 3] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LoxPelt: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'rug_wolf',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    group: 'rug',
    tier: 4,
    comfort: { value: 1, group: 'carpet' },
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [3.5, 0, 2.5] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { WolfPelt: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'rug_deer',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    group: 'rug',
    tier: 1,
    comfort: { value: 1, group: 'carpet' },
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [3, 0, 2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { DeerHide: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'jute_carpet',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    group: 'rug',
    tier: 4,
    comfort: { value: 1, group: 'carpet' },
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [4, 0, 3] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { JuteRed: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'rug_straw',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    group: 'rug',
    tier: 5,
    comfort: { value: 1, group: 'carpet' },
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [2, 0.05, 2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { Barley: 1, Flax: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'jute_carpet_blue',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    group: 'rug',
    tier: 6,
    comfort: { value: 1, group: 'carpet' },
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [3, 0, 3] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { JuteBlue: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'rug_hare',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    group: 'rug',
    tier: 6,
    comfort: { value: 1, group: 'carpet' },
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [1.8, 0, 1.1] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { ScaleHide: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'rug_asksvin',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    group: 'rug',
    tier: 7,
    comfort: { value: 1, group: 'carpet' },
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [1.8, 0, 1.1] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { AskHide: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner01',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 2,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Coal: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner02',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 2,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Blueberries: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner03',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 2,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Raspberry: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner04',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 3,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Bloodbag: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner05',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 3,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Guck: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner06',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 5,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Blueberries: 2, Raspberry: 2, Cloudberry: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner07',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 5,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Blueberries: 2, Cloudberry: 3 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner08',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 2,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Dandelion: 4, Coal: 2 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner09',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 2,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Blueberries: 2, Raspberry: 3 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner10',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 5,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Carrot: 2, Cloudberry: 3 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_banner11',
    group: 'banner',
    base: false,
    type: 'piece',
    subtype: 'decoration',
    tier: 5,
    comfort: { value: 1, group: 'banner' },
    piece: { target: 'none', water: undefined, size: [1.2, 3, 0.2] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: { type: 'craft_piece', materials: { LeatherScraps: 6, FineWood: 2, Coal: 2, Cloudberry: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_cloth_hanging_door',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 4,
    emoji: '',
    piece: { target: 'random', water: undefined, size: [0.6, 1.2, 0.4] },
    wear: { ...woodRoofStructureWear, hp: 50 },
    recipe: {
      type: 'craft_piece',
      materials: { JuteRed: 4, FineWood: 1 },
      station: 'piece_workbench',
    },
  },
  {
    id: 'piece_cloth_hanging_door_blue',
    base: false,
    type: 'piece',
    subtype: 'structure',
    disabled: true,
    tier: 4,
    emoji: '',
    piece: { target: 'random', water: undefined, size: [0, 2.75, 1.2] },
    wear: wearStructure(50, { ...woodResist, blunt: 'resistant' }, MaterialType.Wood),
    recipe: {
      type: 'craft_piece',
      materials: { JuteBlue: 4, FineWood: 1 },
      station: 'piece_workbench',
    },
  },
  {
    id: 'piece_cloth_hanging_door_blue2',
    base: false,
    type: 'piece',
    subtype: 'structure',
    disabled: true,
    tier: 4,
    emoji: '',
    piece: { target: 'random', water: undefined, size: [2, 0.9, 0.1] },
    wear: wearStructure(50, { ...woodResist, blunt: 'resistant' }, MaterialType.Wood),
    recipe: {
      type: 'craft_piece',
      materials: { JuteBlue: 4, FineWood: 1 },
      station: 'piece_workbench',
    },
  },
  {
    id: 'piece_beehive',
    components: ['Beehive'],
    base: false,
    type: 'piece',
    subtype: 'craft',
    tier: 1,
    emoji: '🐝🏠',
    craft: {},
    piece: { target: 'primary', water: undefined, size: [1.8, 1.7, 1.8] },
    wear: { ...woodStructureWear, hp: 100 },
    recipe: { type: 'craft_piece', materials: { Wood: 10, QueenBee: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_sapcollector',
    base: false,
    type: 'piece',
    components: ['SapCollector'],
    subtype: 'craft',
    tier: 6,
    craft: {},
    blockingPieces: { pieces: ['piece_sapcollector'], radius: 2 },
    SapCollector: {
      from: 'YggdrasilRoot',
      secPerUnit: 60,
      maxLevel: 10,
      item: 'Sap',
    },
    piece: { target: 'primary', water: false, onlyOnFlat, size: [0.9, 3.3, 0.5] },
    wear: wearStructure(100, mods([0, 0, 0, 4, 4, 0, 0, 0, 3, 3]), undefined, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 10, BlackMetal: 5, DvergrNeedle: 1 }, station: 'piece_workbench' },
  },
  ashwoodStructure('ashwood_wall_2x2', [2, 0.4, 2], 2),
  ashwoodStructure('ashwood_halfwall_1x2', [1, 0.4, 2], 1),
  ashwoodStructure('ashwood_quarterwall_1x1', [1, 0.4, 1], 1),
  ashwoodStructure('ashwood_arch_top', [1, 0.4, 1], 2), // arched wall
  ashwoodStructure('ashwood_decowall_2x2', [2, 0.4, 2], 2),
  ashwoodStructure('ashwood_decowall_tree', [2, 0.2, 1], 2),
  ashwoodStructure('ashwood_decowall_divider', [1, 0.15, 2], 2),
  ashwoodStructure('ashwood_floor_2x2', [2, 2, 0], 4),
  ashwoodStructure('ashwood_floor_1x1', [1, 1, 0], 2),
  ashwoodStructure('ashwood_deco_floor', [2, 2, 0], 4),
  ashwoodStructure('ashwood_arch_big', [2, 0, 2], 2),
  ashwoodStructure('ashwood_beam_1m', [1, 0.4, 0.4], 2),
  ashwoodStructure('ashwood_beam_2m', [2, 0.4, 0.4], 4),
  ashwoodStructure('ashwood_pole_1m', [0.4, 0.4, 1], 2),
  ashwoodStructure('ashwood_pole_2m', [0.4, 0.4, 2], 4),
  ashwoodStructure('ashwood_wall_beam_26', [2, 0.4, 1], 1),
  ashwoodStructure('ashwood_wall_cross_26', [2, 0.4, 1], 1),
  ashwoodStructure('ashwood_wall_beam_45', [2, 0.4, 2], 1),
  ashwoodStructure('ashwood_wall_cross_45', [2, 0.4, 2], 1),
  ashwoodStructure('ashwood_wall_roof_26', [2, 0.4, 1], 1),
  ashwoodStructure('ashwood_wall_roof_26_upsidedown', [2, 0.4, 1], 1),
  ashwoodStructure('ashwood_wall_roof_45', [2, 0.4, 2], 1),
  ashwoodStructure('ashwood_wall_roof_45_upsidedown', [2, 0.4, 2], 1),
  ashwoodStructure('ashwood_stair', [2, 1, 1], 2),

  graustenStructure('Piece_grausten_stone_ladder', 400, [1, 3, 2], 5),
  graustenStructure('piece_grausten_stonestair', 1500, [2, 1, 2], 8),
  graustenStructure('Piece_grausten_floor_1x1', 500, [1, 0.5, 1], 2),
  graustenStructure('Piece_grausten_floor_2x2', 1000, [2, 0.5, 2], 4),
  graustenStructure('Piece_grausten_floor_4x4', 2000, [4, 0.5, 4], 8),
  graustenStructure('Piece_grausten_pillarbase_small', 200, [0.5, 1, 0.5], 1),
  graustenStructure('Piece_grausten_pillarbase_medium', 400, [1, 1, 1], 3),
  graustenStructure('Piece_grausten_pillarbase_tapered', 500, [1, 2, 1], 5),
  graustenStructure('Piece_grausten_pillarbase_tapered_inverted', 500, [1, 2, 1], 5),
  graustenStructure('Piece_grausten_pillarbeam_small', 200, [0.5, 1, 0.5], 1),
  graustenStructure('Piece_grausten_pillarbeam_medium', 400, [1, 1, 1], 3),
  graustenStructure('Piece_grausten_pillar_arch_small', 200, [1.25, 0.5, 1.25], 2),
  graustenStructure('Piece_grausten_pillar_arch', 400, [2.5, 1, 2.5], 4),
  graustenStructure('Piece_grausten_wall_arch', 400, [2, 2, 0.4], 2),
  graustenStructure('Piece_grausten_wall_arch_inverted', 400, [2, 2, 0.4], 2),
  graustenStructure('Piece_grausten_wall_1x2', 500, [1, 2, 0.4], 4),
  graustenStructure('Piece_grausten_wall_2x2', 1000, [2, 2, 0.4], 6),
  graustenStructure('Piece_grausten_wall_4x2', 2000, [4, 2, 0.4], 12),
  graustenStructure('Piece_grausten_window_2x2', 1000, [2, 2, 0.4], 4),
  graustenStructure('Piece_grausten_window_4x2', 2000, [4, 2, 0.4], 10),
  graustenStructure('piece_grausten_roof_45', 500, [2, 2, 2], 5),
  graustenStructure('piece_grausten_roof_45_corner', 500, [2, 2, 2], 5),
  graustenStructure('piece_grausten_roof_45_corner2', 500, [2, 2, 2], 5),
  graustenStructure('piece_grausten_roof_45_arch', 500, [2, 2, 2], 5),
  graustenStructure('piece_grausten_roof_45_arch_corner', 500, [2, 2, 2], 5),
  graustenStructure('piece_grausten_roof_45_arch_corner2', 500, [2, 2, 2], 5),
  {
    id: 'Piece_flametal_beam',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 7,
    piece: { target: 'random', water: undefined, size: [2, 0.25, 0.25] },
    wear: wearStructure(1000, mods([0, 1, 1, 0, 1, 1, 1, 1, 4, 4]), MaterialType.Iron, { ashResist: true, ashImmune: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { FlametalNew: 2 }, station: 'blackforge', },
  },
  {
    id: 'Piece_flametal_pillar',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 7,
    piece: { target: 'random', water: undefined, size: [0.25, 2, 0.25] },
    wear: wearStructure(1000, mods([0, 1, 1, 0, 1, 1, 1, 1, 4, 4]), MaterialType.Iron, { ashResist: true, ashImmune: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { FlametalNew: 2 }, station: 'blackforge', },
  },
  {
    id: 'fermenter',
    base: true,
    type: 'piece',
    components: ['Fermenter'],
    subtype: 'craft',
    tier: 2,
    craft: {
      requiresRoof,
    },
    piece: { target: 'primary', water: false, onlyOnFlat, size: [1.7, 2.2, 2] },
    wear: wearStructure(1000, craftStationResist, MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 30, Bronze: 5, Resin: 10 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_gift1', season: 'christmas',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 1,
    emoji: '🎁',
    space: [1, 1],
    piece: { target: 'none', water: false, onlyOnFlat, size: [0.6, 0.35, 0.4] },
    wear: wearStructure(10, mods([1, 1, 1, 1, 1, 1, 1, 0, 3, 3]), MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 2, BoneFragments: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_gift2', season: 'christmas',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 1,
    emoji: '🎁',
    space: [1, 1],
    piece: { target: 'none', water: false, onlyOnFlat, size: [0.75, 0.4, 0.5] },
    wear: wearStructure(10, mods([1, 1, 1, 1, 1, 1, 1, 0, 3, 3]), MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 3, Dandelion: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_gift3', season: 'christmas',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 1,
    emoji: '🎁',
    space: [1, 1],
    piece: { target: 'none', water: false, onlyOnFlat, size: [0.9, 0.5, 0.6] },
    wear: wearStructure(10, mods([1, 1, 1, 1, 1, 1, 1, 0, 3, 3]), MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 4, Raspberry: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_pot1',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 7,
    emoji: '⚱',
    space: [1, 2],
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [0.5, 1, 0.5] },
    wear: wearStructure(40, mods([2, 0, 1, 0, 2, 1, 1, 1, 3, 3]), undefined, { noRoof: false, ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Pot_Shard_Green: 4, CharcoalResin: 1 }, station: null },
  },
  {
    id: 'piece_pot2',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 7,
    emoji: '⚱',
    space: [1, 3],
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [0.5, 1, 0.5] },
    wear: wearStructure(50, mods([2, 0, 1, 0, 2, 1, 1, 1, 3, 3]), undefined, { noRoof: false, ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Pot_Shard_Green: 5, CharcoalResin: 1 }, station: null },
  },
  {
    id: 'piece_pot3',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 7,
    emoji: '⚱',
    space: [1, 1],
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [0.5, 1, 0.5] },
    wear: wearStructure(30, mods([2, 0, 1, 0, 2, 1, 1, 1, 3, 3]), undefined, { noRoof: false, ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Pot_Shard_Green: 3, CharcoalResin: 1 }, station: null },
  },
  {
    id: 'piece_pot1_red',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 7,
    emoji: '⚱',
    space: [1, 2],
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [0.5, 1, 0.5] },
    wear: wearStructure(40, mods([2, 0, 1, 0, 2, 1, 1, 1, 3, 3]), undefined, { noRoof: false, ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Pot_Shard_Red: 4, CharcoalResin: 1 }, station: null },
  },
  {
    id: 'piece_pot2_red',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 7,
    emoji: '⚱',
    space: [1, 3],
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [0.5, 1, 0.5] },
    wear: wearStructure(50, mods([2, 0, 1, 0, 2, 1, 1, 1, 3, 3]), undefined, { noRoof: false, ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Pot_Shard_Red: 5, CharcoalResin: 1 }, station: null },
  },
  {
    id: 'piece_pot3_red',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'chest',
    tier: 7,
    emoji: '⚱',
    space: [1, 1],
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [0.5, 1, 0.5] },
    wear: wearStructure(30, mods([2, 0, 1, 0, 2, 1, 1, 1, 3, 3]), undefined, { noRoof: false, ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Pot_Shard_Red: 3, CharcoalResin: 1 }, station: null },
  },
  {
    id: 'piece_xmastree', season: 'christmas',
    base: false,
    type: 'piece',
    tier: 2,
    emoji: '🎄',
    subtype: 'decoration',
    comfort: { value: 1 },
    piece: { target: 'random', water: undefined, size: [1.6, 2.7, 1.6] },
    wear: wearStructure(50, woodResist, MaterialType.Wood, { noRoof: false }),
    PointLight: { color: '#FFAD31', range: 3, intensity: 1.9 },
    recipe: { type: 'craft_piece', materials: { Wood: 10, FirCone: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_xmascrown', season: 'christmas',
    base: false,
    type: 'piece',
    tier: 2,
    emoji: '👑',
    subtype: 'misc',
    piece: { target: 'random', notOnFloor, water: undefined, size: [0.5, 0.5, 0.2] },
    wear: wearStructure(50, woodResist, undefined),
    recipe: { type: 'craft_piece', materials: { PineCone: 4, JuteRed: 1, FineWood: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_xmasgarland', season: 'christmas',
    base: false,
    type: 'piece',
    tier: 2,
    emoji: '🎀',
    subtype: 'misc',
    piece: { target: 'random', notOnFloor, water: undefined, size: [2.2, 0.6, 0.4] },
    wear: wearStructure(50, woodResist, undefined),
    recipe: { type: 'craft_piece', materials: { FineWood: 2, PineCone: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_mistletoe', season: 'christmas',
    base: false,
    type: 'piece',
    tier: 2,
    emoji: '🌿',
    subtype: 'misc',
    piece: { target: 'random', onlyCeiling, water: undefined, size: [0.7, 0.7, 0.7] },
    wear: wearStructure(50, woodResist, undefined),
    recipe: { type: 'craft_piece', materials: { FineWood: 1, JuteRed: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_maypole', season: 'midsummer',
    base: false,
    type: 'piece',
    tier: 2,
    subtype: 'decoration',
    comfort: { value: 1 },
    piece: { target: 'random', water: undefined, size: [4.2, 5, 0.9] },
    wear: wearStructure(50, woodResist, undefined, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Wood: 10, Dandelion: 4, Thistle: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_jackoturnip', season: 'helloween',
    base: false,
    type: 'piece',
    components: ['Fireplace'],
    subtype: 'fireplace',
    emoji: '🎃',
    tier: 1,
    fireplace: {
      fuel: 'Resin',
      capacity: 6,
      burnTime: 20000,
      minHeightAbove: 0.76,
      warmRadius: 0,
      lightRadius: 0,
      smoke: false,
      fireworks: false,
      ignite: { interval: 0, chance: 0, spread: 4, capsuleRadius: 0 },
    },
    piece: { target: 'primary', water: undefined, size: [0.6, 0.6, 0.6] },
    wear: wearStructure(200, torchResist, undefined, { noRoof: false }),
    PointLight: { color: '#FF9E7B', range: 4, intensity: 3 },
    recipe: { type: 'craft_piece', materials: { Turnip: 4, Resin: 2 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_chest_treasure',
    base: false,
    type: 'piece',
    subtype: 'misc',
    tier: 4,
    piece: { target: 'primary', water: false, onlyOnFlat, size: [1.1, 0.5, 0.7] },
    wear: wearStructure(200, mods([0, 0, 1, 0, 0, 2, 1, 0, 3, 3]), undefined, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Coins: 99, Ruby: 5, SilverNecklace: 2, FineWood: 8, Silver: 2 }, station: 'piece_workbench' },
  },
  {
    id: 'treasure_pile',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 2,
    emoji: '',
    piece: { target: 'random', water: undefined, onlyOnFlat, allowedInDungeons, size: [2.3, 0.4, 2.3] },
    wear: {
      hp: 100,
      damageModifiers: mods([0, 0, 0, 0, 0, 1, 3, 3, 3, 3]),
      noRoof: false,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Coins: 999, }, station: null, }
  },
  {
    id: 'treasure_stack',
    base: false,
    type: 'piece',
    subtype: 'misc',
    group: 'stack',
    tier: 2,
    emoji: '',
    piece: { target: 'random', water: false, onlyOnFlat, allowedInDungeons, size: [0.3, 0.5, 0.3] },
    wear: {
      hp: 5,
      damageModifiers: mods([2, 2, 2, 2, 2, 1, 3, 3, 3, 3]),
      noRoof: false,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Coins: 99, }, station: null, }
  },
  {
    id: 'piece_bathtub',
    base: false,
    type: 'piece',
    components: ['Smelter'],
    subtype: 'decoration',
    emoji: '🛁',
    tier: 5,
    comfort: { value: 2 },
    // lightRadius: 4,
    piece: { target: 'random', water: false, onlyOnFlat, size: [3.2, 2.3, 3.6] },
    wear: wearStructure(400, woodResist, MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Wood: 20, Tar: 6, Iron: 10, Stone: 8 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_barber',
    base: false,
    type: 'piece',
    components: [],
    subtype: 'decoration',
    emoji: '💈',
    tier: 2,
    comfort: { value: 2, group: 'chair' },
    piece: { target: 'random', water: false, onlyOnFlat, size: [2, 1.5, 1.3] },
    wear: wearStructure(200, woodResist, MaterialType.Wood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FineWood: 10, BarberKit: 1, BronzeNails: 5, TrollHide: 5 }, station: 'piece_workbench' },
  },
  {
    id: 'crystal_wall_1x1',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 3,
    piece: { target: 'random', water: undefined, size: [1, 1, 0.3] },
    wear: {
      hp: 10,
      damageModifiers: mods([0, 0, 0, 2, 0, 0, 0, 0, 3, 3]),
      noRoof: false,
    },
    recipe: { type: 'craft_piece', materials: { Crystal: 2 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_cartographytable',
    components: ['MapTable'],
    base: false,
    type: 'piece',
    subtype: 'misc',
    tier: 2,
    piece: { target: 'none', water: false, onlyOnFlat, size: [4.1, 1.6, 2.5] },
    wear: wearStructure(100, woodResist, undefined),
    recipe: { type: 'craft_piece', materials: { FineWood: 10, BoneFragments: 10, Bronze: 2, LeatherScraps: 5, Raspberry: 4 }, station: 'piece_workbench' },
  },
  {
    id: 'incinerator',
    base: false,
    type: 'piece',
    components: ['Container'],
    subtype: 'misc',
    tier: 2,
    emoji: '',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [2.4, 11.5, 1.7] },
    wear: {
      hp: 1000,
      damageModifiers: mods([1, 1, 1, 1, 1, 1, 1, 3, 3, 3]),
      noRoof: true,
      noSupport: true,
    },
    recipe: { type: 'craft_piece', materials: { Iron: 8, Copper: 4, Thunderstone: 1 }, station: 'forge', }
  },
  {
    id: 'ArmorStand',
    base: false,
    type: 'piece',
    components: ['ArmorStand'], // VisEquipment, but ArmorStand covers things for work with ZDO
    subtype: 'decoration',
    comfort: { value: 1 },
    tier: 3,
    piece: { target: 'none', water: undefined, onlyOnFlat, size: [1.2, 2, 1.2] },
    wear: wearStructure(600, mods([1, 1, 1, 1, 1, 1, 1, 0, 3, 3]), undefined),
    recipe: {
      type: 'craft_piece',
      materials: { FineWood: 8, IronNails: 4, LeatherScraps: 2 },
      station: 'piece_workbench',
    },
  },
  {
    id: 'piece_magetable',
    base: true,
    type: 'piece',
    components: ['CraftingStation'],
    group: 'craft_station',
    subtype: 'craft',
    tier: 6,
    emoji: '🧙‍♂️',
    piece: { target: 'primary', water: false, onlyOnFlat, size: [3.4, 2.7, 3.4] },
    craft: {
      skill: SkillType.Crafting,
      requiresRoof,
    },
    wear: {
      hp: 200,
      damageModifiers: mods([0, 0, 1, 2, 0, 0, 0, 0, 3, 3]),
      noRoof: false,
      noSupport: true,
      materialType: MaterialType.Wood,
    },
    PointLight: { color: '#FF31DC', range: 3, intensity: 3 },
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 20, BlackMetal: 10, BlackCore: 5, Eitr: 5 }, station: 'piece_workbench', }
  },
  {
    id: 'piece_magetable_ext',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 6,
    emoji: '🧰',
    piece: {
      target: 'primary',
      water: false,
      onlyOnFlat,
      requiredSpace: 2,
      size: [1, 1, 1],
    },
    extends: {
      id: 'piece_magetable',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: undefined,
    },
    recipe: { type: 'craft_piece', materials: { BlackMarble: 10, TrophySkeleton: 3, Eitr: 10, Resin: 15 }, station: 'piece_workbench' }
  },
  {
    id: 'piece_magetable_ext2',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 6,
    emoji: '🧰',
    piece: {
      target: 'primary',
      water: false,
      onlyOnFlat,
      requiredSpace: 2,
      size: [0.6, 1.1, 0.6],
    },
    extends: {
      id: 'piece_magetable',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: undefined,
    },
    recipe: { type: 'craft_piece', materials: { BlackMarble: 10, YggdrasilWood: 5, Eitr: 10, Resin: 15 }, station: 'piece_workbench' }
  },
  {
    id: 'piece_magetable_ext3',
    base: false,
    type: 'piece',
    components: ['CraftingStationExtension'],
    subtype: 'craft_ext',
    tier: 7,
    emoji: '🧰',
    piece: {
      target: 'primary',
      water: false,
      requiredSpace: 2,
      size: [0.6, 1.1, 0.6],
    },
    extends: {
      id: 'piece_magetable',
      distance: 5,
    },
    wear: {
      hp: 100,
      damageModifiers: damageModifieresExtensions,
      noRoof: true,
      noSupport: true,
      materialType: undefined,
    },
    recipe: { type: 'craft_piece', materials: { CelestialFeather: 8, TrophyAsksvin: 1, Eitr: 10, Blackwood: 3 }, station: 'piece_workbench' }
  },
  {
    id: 'piece_wisplure',
    base: true,
    type: 'piece',
    components: ['WispSpawner'],
    subtype: 'craft',
    tier: 6,
    demister: 6,
    piece: { target: 'primary', water: false, onlyOnFlat, size: [1.5, 5, 1.5] },
    craft: {
      requiresRoof: false,
    },
    wear: {
      hp: 100,
      damageModifiers: woodResist,
      noRoof: true,
      noSupport: true,
      materialType: MaterialType.Stone,
    },
    recipe: { type: 'craft_piece', materials: { Stone: 10, YagluthDrop: 1 }, station: 'piece_stonecutter', }
  },
  {
    id: 'piece_dvergr_pole',
    base: false,
    type: 'piece',
    disabled: true,
    subtype: 'structure',
    tier: 6,
    emoji: '',
    piece: { target: 'random', water: undefined, size: [0.6, 0.6, 4] },
    wear: wearStructure(600, woodResist, MaterialType.HardWood),
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 4, Copper: 1 }, station: 'blackforge', },
  },
  {
    id: 'piece_dvergr_wood_wall',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 6,
    piece: { target: 'random', water: undefined, size: [2.2, 2, 0.7] },
    wear: wearStructure(1000, woodResist, MaterialType.HardWood),
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 20, Copper: 5 }, station: 'blackforge', },
  },
  {
    id: 'piece_dvergr_spiralstair',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 6,
    emoji: '',
    piece: { target: 'none', water: undefined, size: [2.3, 1.2, 2.3] },
    wear: wearStructure(800, woodResist, MaterialType.HardWood),
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 7, Copper: 7 }, station: 'blackforge', },
  },
  {
    id: 'piece_dvergr_spiralstair_right',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 6,
    emoji: '',
    piece: { target: 'none', water: undefined, size: [2.3, 1.2, 2.3] },
    wear: wearStructure(800, woodResist, MaterialType.HardWood),
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 7, Copper: 7 }, station: 'blackforge', },
  },
  {
    id: 'piece_dvergr_sharpstakes',
    base: false,
    type: 'piece',
    subtype: 'structure',
    tier: 6,
    Aoe: { damage: dmg({ pierce: 15 }), self: 34 },
    piece: { target: 'random', water: undefined, size: [3.8, 2.7, 2.5] },
    wear: wearStructure(500, { ...woodResist, frost: 'resistant', lightning: 'resistant' }, MaterialType.HardWood, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { YggdrasilWood: 5, Copper: 5 }, station: 'blackforge', },
  },
  {
    id: 'piece_dvergr_lantern',
    base: true,
    type: 'piece',
    subtype: 'decoration',
    comfort: { value: 0, group: 'fire' },
    tier: 6,
    piece: { target: 'random', water: false, onlyOnFlat, size: [0.7, 1.3, 1.1] },
    wear: wearStructure(200, mods([0, 0, 1, 2, 0, 1, 1, 1, 3, 3]), MaterialType.Iron, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Copper: 2, Lantern: 1, Chain: 1 }, station: 'blackforge' },
  },
  {
    id: 'piece_dvergr_lantern_pole',
    base: true,
    type: 'piece',
    subtype: 'decoration',
    comfort: { value: 0, group: 'fire' },
    tier: 6,
    piece: { target: 'random', water: false, onlyOnFlat, size: [0.4, 3.5, 1] },
    wear: wearStructure(300, mods([0, 0, 1, 2, 0, 1, 1, 1, 3, 3]), MaterialType.Iron, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Copper: 2, Lantern: 1, Chain: 1 }, station: 'blackforge' },
  },
  {
    id: 'Candle_resin',
    base: false,
    type: 'piece',
    subtype: 'fireplace',
    group: 'torch',
    tier: 3,
    fireplace: {
      fuel: 'Resin',
      capacity: 3,
      burnTime: 5000,
      minHeightAbove: 0.76,
      warmRadius: 0,
      lightRadius: 3,
      smoke: false,
      fireworks: true,
      ignite: { interval: 25, chance: 0.0025, spread: 1, capsuleRadius: 0.1 },
    },
    piece: { target: 'random', water: undefined, onlyOnFlat, size: [0.15, 0.15, 0.3] },
    wear: wearStructure(5, mods([2, 2, 2, 2, 2, 1, 3, 3, 3, 3]), undefined, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { Resin: 1, CandleWick: 1 }, station: 'piece_workbench' },
  },
  {
    id: 'piece_Lavalantern',
    base: true,
    type: 'piece',
    subtype: 'decoration',
    comfort: { value: 1 },
    PointLight: { range: 12, intensity: 1.5, color: '#ff9e7b' },
    tier: 7,
    piece: { target: 'random', water: false, onlyOnFlat, size: [0.4, 0.8, 0.4] },
    wear: wearStructure(100, mods([0, 0, 1, 2, 0, 1, 1, 1, 3, 3]), MaterialType.Iron, { noRoof: false }),
    recipe: { type: 'craft_piece', materials: { FlametalNew: 1, ProustitePowder: 1, SulfurStone: 1 }, station: 'blackforge' },
  },
  {
    id: 'piece_shieldgenerator',
    base: true,
    type: 'piece',
    subtype: 'structure',
    tier: 7,
    piece: { target: 'random', water: false, onlyOnFlat, size: [2.2, 6.5, 2.2] },
    wear: wearStructure(1500, mods([1, 1, 1, 1, 0, 3, 3, 0, 3, 3]), MaterialType.Stone, { noRoof: false, ashResist: true, burnable: false }),
    recipe: { type: 'craft_piece', materials: { Stone: 5, ShieldCore: 1, CeramicPlate: 5 }, station: 'piece_workbench' },
    ShieldGenerator: {
      fuelItems: ['BoneFragments', 'CharredBone'],
      maxFuel: 10,
      fuelPerDamage: 0.002,
    },
  },
  // Non-player-ones
  {
    id: 'CastleKit_groundtorch',
    iconId: 'piece/piece_groundtorch',
    base: false,
    burnable: false,
    type: 'piece',
    subtype: 'external',
    tier: 2,
    wear: wearStructure(20, mods([0, 0, 0, 0, 0, 0, 0, 0, 0, 0]), undefined, { noRoof: false }),
    PointLight: { color: '#FF9E7B', intensity: 2, range: 6 },
  },
  {
    id: 'CastleKit_groundtorch_green',
    iconId: 'piece/piece_groundtorch_green',
    base: false,
    burnable: false,
    type: 'piece',
    subtype: 'external',
    tier: 2,
    wear: wearStructure(20, mods([0, 0, 0, 0, 0, 0, 0, 0, 0, 0]), undefined, { noRoof: false, burnable: false }),
    PointLight: { color: '#B6FF7B', intensity: 2, range: 6 },
  },
  {
    id: 'CastleKit_groundtorch_blue',
    iconId: 'piece/piece_groundtorch_blue',
    base: false,
    type: 'piece',
    subtype: 'external',
    tier: 4,
    wear: wearStructure(20, mods([0, 0, 0, 0, 0, 0, 0, 0, 0, 0]), undefined, { noRoof: false }),
    PointLight: { color: '#BFFEFF', intensity: 2, range: 6 },
  },
  {
    id: 'CastleKit_metal_groundtorch_unlit',
    iconId: 'piece/piece_groundtorch',
    base: false,
    type: 'piece',
    subtype: 'external',
    tier: 4,
    wear: wearStructure(20, mods([0, 0, 0, 0, 0, 1, 1, 0, 3, 3]), undefined, { noRoof: false }),
    PointLight: { color: '#FF9E7B', intensity: 1.5, range: 10 },
  },
];

pieces.forEach(p => {
  if (p.components == null) p.components = [];
  p.components.push('WearNTear', 'Piece');
});
